"use client";

import { hrefBehindCurrentEntry } from "@/lib/navigation-history";
import { prepareRouteScrollRestore } from "@/lib/navigation-scroll";

/** The slice of Next's router this needs; kept structural so tests can fake it. */
type BackRouter = {
  back: () => void;
  push: (href: string) => void;
};

/**
 * Go back to `backHref` — the one place that decides how.
 *
 * Every back affordance in the app resolves the same intent (`?from=`, falling
 * back to a route's natural parent) and then has to choose between two very
 * different mechanics:
 *
 *   - `router.back()` pops the entry behind us, which is the only path that
 *     restores Next's own router cache and the browser's scroll — but it goes
 *     wherever that entry happens to be, which is not necessarily `backHref`.
 *   - `router.push(backHref)` always lands on the right URL, as a fresh
 *     navigation that starts at the top of a rebuilt page.
 *
 * So: pop only when the entry behind us *is* the href we mean, and stage the
 * scroll restore either way. Staging matters most on the push path, which is
 * precisely the one the browser will not restore for us — the hiive and venue
 * pages pushed unconditionally and never staged, so every back out of them
 * dropped the reader at the top of /hiives with its carousels rewound.
 *
 * The restore is staged before either branch because `prepareRouteScrollRestore`
 * only copies a remembered record into the pending slot; if nothing was
 * captured for `backHref` it is a no-op, and if the browser does restore the
 * position itself the restorer reads it as already reached and stops on its
 * first frame.
 */
export function goBackTo(router: BackRouter, backHref: string) {
  prepareRouteScrollRestore(backHref);

  if (hrefBehindCurrentEntry() === backHref) {
    router.back();
    return;
  }

  router.push(backHref);
}
